import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ComponentAdapterCounterHeating } from "../../class/adapters/component-adapter-counter-heating";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { IWidgetExpandable } from "../../interfaces/IWidgetExpandable";
import { CounterHeaderData } from "../../class/counter-header-data";
import {
  GasCounterMeasurementType,
  HeatingCounterMeasurementType,
} from "../../enum/enumCounterMeasurementType";
import { DateHelper } from "../../class/general-classes/date-helper";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { CounterHeaderComponent } from "../counter/counter-header/counter-header.component";
import { CounterHeatMeterComponent } from "../counter/counter-heat-meter/counter-heat-meter.component";
import { IonIcon } from "@ionic/angular";

@Component({
    selector: "app-widget-counter-heating",
    templateUrl: "./widget-counter-heating.component.html",
    styleUrls: [
        "./widget-counter-heating.component.scss",
        "../../styles/shared-global.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterHeaderComponent, CounterHeatMeterComponent, IonIcon]
})
export class WidgetCounterHeatingComponent
  extends WidgetParent<ComponentAdapterCounterHeating, SmartPaletteCounter>
  implements IWidgetExpandable
{
  private _currentMonthValue: string = "";
  private _previousMonthValue: string = "";

  public isExpanded: boolean = false;
  public areChartsVisible: boolean = false;
  public headerData: CounterHeaderData = null;

  /**
   * Open or close the widget
   */
  public onClickOpenCloseWidget(): void {
    if (!this.isExpanded) {
      this.onExpandWidget();
    } else {
      this.onCollapseWidget();
    }
  }

  onExpandWidget(): void {
    this.isExpanded = true;
  }

  onCollapseWidget(): void {
    this.isExpanded = false;
  }

  protected override onLanguageChange(): void {
    const now = new Date();
    this._currentMonthValue = this._localeService.formatDate(now, "month").toUpperCase();
    this._previousMonthValue = this._localeService.formatDate(DateHelper.subtract(now, 1, "month"), "month").toUpperCase();

    if (this.headerData) {
      this.headerData.subtitle = this._currentMonthValue;
      this.headerData.title1 = this._translateService.instant("dictionary.thermalEnergy").toUpperCase();
      this.headerData.title2 = this._translateService.instant("dictionary.thermalPower").toUpperCase();
      this.headerData.title3 = this._translateService.instant("dictionary.volume").toUpperCase();
    }

    if (this.componentAdapter) {
      this.componentAdapter.CurrentMonth = this._currentMonthValue;
      this.componentAdapter.PreviousMonth = this._previousMonthValue;
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteCounter {
    // All counters share the same brand-derived palette (see setWidgetSmartPaletteCounterDefault).
    return this.setWidgetSmartPaletteCounterDefault();
  }

  protected onComponentLoad(): void {}

  protected onSmartComponentSuccessLoaded(): void {
    const factory = new ComponentAdapterFactory();
    this.loadSummaryData();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterCounterHeating,
      this._headerSummary
    );

    this.componentAdapter.fillMeasurementMap(
      this._listSmartComponentCounterMeasurements,
      HeatingCounterMeasurementType,
      this.defaultDecimalDigits,
      this._localeService.locale()
    );

    this.prepareHeaderData();
  }

  protected onComponentDestroy(): void {}

  protected onSmartComponentErrorLoading(): void {}

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {}

  /**
   * This method prepare and initialise the data passed to the header compoent. These data are:
   * + Taken from the previously fetched property.set of the SmartComponent (Name)
   * + Defined locally as localised text strings based on the counter sub-type (Titles)
   * + Calculated locally (Subtitle)
   */
  private prepareHeaderData(): void | boolean {
    const dataToHeader: CounterHeaderData = new CounterHeaderData();
    if (this.smartComponent.summary) {
      dataToHeader.titleWidget = this._translateService.instant("dictionary.consumptionDistribution");
      dataToHeader.subtitle = "";
    } else {
      dataToHeader.titleWidget = this.componentAdapter.Name;
      dataToHeader.subtitle = this._currentMonthValue;
    }

    //If component title is empty show error loading component
    if (!dataToHeader.titleWidget) {
      return (this._errorLoading = true);
    }

    if (this.componentAdapter.EnergyHeat) {
      dataToHeader.title1 = this._translateService.instant("dictionary.thermalEnergy").toUpperCase();
      dataToHeader.value1 =
        this.componentAdapter.MeasurementMap.get(
          HeatingCounterMeasurementType[
            HeatingCounterMeasurementType.HEAT_THERMAL_ENERGY_LAST_MONTH
          ]
        ) || "---";
    }

    if (this.componentAdapter.Power && !this.componentAdapter.Summary) {
      dataToHeader.title2 = this._translateService.instant("dictionary.thermalPower").toUpperCase();
      dataToHeader.value2 =
        this.componentAdapter.MeasurementMap.get(
          HeatingCounterMeasurementType[
            HeatingCounterMeasurementType.HEAT_ACTIVE_POWER
          ]
        ) || "---";
    }

    if (this.componentAdapter.Volume) {
      dataToHeader.title3 = this._translateService.instant("dictionary.volume").toUpperCase();
      dataToHeader.value3 =
        this.componentAdapter.MeasurementMap.get(
          HeatingCounterMeasurementType[
            HeatingCounterMeasurementType.HEAT_VOLUME_LAST_MONTH
          ]
        ) || "---";
    }
    this.headerData = dataToHeader;

    this.isExpanded = this._isExpanded;
    this.areChartsVisible = this._isExpanded ? true : false;
  }
}
